import { css, cx } from "@emotion/css";
import { useFormik } from "formik";
import { Check, Folder } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { FormTextField } from "src/components/Default/LekUI/TextField";
import type { FolderDetails } from "src/components/Folders/types";
import {
	Modal,
	ModalFooter,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { useFontSize } from "src/hooks/useFontSize";
import { ModalMaxWidth } from "src/types/enums";
import * as Yup from "yup";

const FOLDER_COLORS = [
	"68, 65, 147",
	"107, 65, 147",
	"147, 65, 143",
	"147, 65, 103",
	"147, 68, 65",
	"147, 107, 65",
	"140, 147, 65",
	"99, 147, 65",
	"65, 147, 77",
	"65, 147, 118",
	"65, 138, 147",
	"65, 96, 147",
];

interface FolderModalProps {
	onClose: () => void;
	initialName?: string;
	initialColor?: string;
	onSubmit: (name: string, details: FolderDetails | null) => Promise<void>;
	title: string;
	submitLabel: string;
	isLoading: boolean;
}

export const FolderModal = ({
	onClose,
	initialName = "",
	initialColor,
	onSubmit,
	title,
	submitLabel,
	isLoading,
}: FolderModalProps) => {
	const { t } = useTranslation();
	const { modalHeaderFontSizeClass, fontSizeClass } = useFontSize();

	const { handleSubmit, setFieldValue, values, errors } = useFormik<{
		name: string;
		color: string | null;
	}>({
		initialValues: {
			name: initialName,
			color: initialColor ?? null,
		},
		onSubmit: async ({ name, color }, { resetForm }) => {
			const details = color ? { color } : null;
			await onSubmit(name, details);
			resetForm();
			onClose();
		},
		validationSchema: Yup.object().shape({
			name: Yup.string()
				.required(t("errors.emptySingleField"))
				.max(255, t("modals.createFolder.maxLengthError")),
		}),
	});

	return (
		<Modal onClose={onClose} maxWidth={ModalMaxWidth.sm}>
			<ModalHeader Icon={Folder} title={title} hasCloseButton />
			<div className="flex flex-col gap-24 w-100 mt-24">
				<div className="flex flex-col gap-8">
					<label
						htmlFor="folder-name"
						className={cx(modalHeaderFontSizeClass, "text-dark-grey")}
					>
						{t("modals.createFolder.name", "Nazwa folderu")}
					</label>
					<FormTextField
						id="folder-name"
						autoFocus
						fullWidth
						error={errors.name}
						value={values.name}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							setFieldValue("name", e.target.value)
						}
						slotProps={{ htmlInput: { "data-test-id": "folder-name-input" } }}
					/>
				</div>
				<div className="flex flex-col gap-12">
					<div className="flex items-center justify-between gap-8">
						<span className={cx(modalHeaderFontSizeClass, "text-dark-grey")}>
							{t("modals.createFolder.colorLabel", "Kolor folderu")}
						</span>
						<span className={cx(fontSizeClass, "text-grey")}>
							{t("modals.editDeck.optional", "opcjonalnie")}
						</span>
					</div>
					<div className="folder-color-grid" data-test-id="folder-color-picker">
						{FOLDER_COLORS.map((color) => (
							<button
								key={color}
								type="button"
								data-test-id="folder-color-option"
								aria-label={`rgb(${color})`}
								aria-pressed={values.color === color}
								onClick={() =>
									setFieldValue("color", values.color === color ? null : color)
								}
								className={cx(
									"folder-color-option",
									css`
										background: rgb(${color});
									`,
								)}
							>
								{values.color === color && (
									<Check className={cx("icon-small", css`color: #fff;`)} />
								)}
							</button>
						))}
					</div>
				</div>
			</div>
			<ModalFooter className="justify-end gap-12 mt-24 pt-16 border-top-1">
				<TextPrimaryButton onClick={onClose} disabled={isLoading}>
					{t("commands.cancel", "Anuluj")}
				</TextPrimaryButton>
				<PrimaryButton
					onClick={() => handleSubmit()}
					disabled={isLoading}
					data-test-id="folder-save-button"
				>
					{submitLabel}
				</PrimaryButton>
			</ModalFooter>
		</Modal>
	);
};
